import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, FileText, Edit, Trash2, Eye, Code, Variable, Info } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [form, setForm] = useState({
    name: '',
    subject: '',
    html_body: '',
    text_body: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.subject || !form.html_body) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/templates/${editingTemplate.id}`, form);
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/templates`, form);
        toast.success('Template created');
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      subject: template.subject,
      html_body: template.html_body,
      text_body: template.text_body || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API_URL}/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const resetForm = () => {
    setForm({ name: '', subject: '', html_body: '', text_body: '' });
    setEditingTemplate(null);
  };

  const extractVariables = (text) => {
    const pattern = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const matches = [...text.matchAll(pattern)];
    return [...new Set(matches.map(m => m[1]))];
  };

  const currentVariables = extractVariables(form.subject + ' ' + form.html_body);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Templates</h1>
          <p className="text-muted-foreground mt-1">Create and manage your email templates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Welcome Email"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject Line</Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="e.g., Hello {contact_name}!"
                  data-testid="template-subject-input"
                />
                <p className="text-xs text-muted-foreground">You can use variables like {'{name}'}, {'{company}'} in the subject too</p>
              </div>
              
              {/* Placeholder Help */}
              <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Using Placeholders</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Type placeholders like <code className="bg-muted px-1 rounded">{'{name}'}</code>, <code className="bg-muted px-1 rounded">{'{company}'}</code>, <code className="bg-muted px-1 rounded">{'{custom1}'}</code> in your email. 
                      These will be replaced with data from your Excel columns when sending.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email Body</Label>
                <RichTextEditor
                  value={form.html_body}
                  onChange={(content) => setForm({ ...form, html_body: content })}
                  placeholder="Write your email here... Use {name}, {company} etc. for personalization"
                />
              </div>
              {currentVariables.length > 0 && (
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Variable className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Detected Variables</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentVariables.map(v => (
                      <Badge key={v} variant="secondary">{`{${v}}`}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} data-testid="save-template-btn">
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates Grid */}
      {templates.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription className="text-xs">
                        {new Date(template.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                  Subject: {template.subject}
                </p>
                {template.variables?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.variables.slice(0, 3).map(v => (
                      <Badge key={v} variant="outline" className="text-xs">{`{${v}}`}</Badge>
                    ))}
                    {template.variables.length > 3 && (
                      <Badge variant="outline" className="text-xs">+{template.variables.length - 3}</Badge>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => setPreviewTemplate(template)}
                    data-testid={`preview-template-${template.id}`}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(template)}
                    data-testid={`edit-template-${template.id}`}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No templates yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Create your first email template to get started</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-muted-foreground">Subject</Label>
              <p className="font-medium">{previewTemplate?.subject}</p>
            </div>
            <div>
              <Label className="text-muted-foreground flex items-center gap-2">
                <Code className="w-4 h-4" />
                HTML Preview
              </Label>
              <div 
                className="mt-2 p-4 rounded-lg border bg-white dark:bg-slate-950"
                dangerouslySetInnerHTML={{ __html: previewTemplate?.html_body || '' }}
              />
            </div>
            {previewTemplate?.text_body && (
              <div>
                <Label className="text-muted-foreground">Plain Text</Label>
                <pre className="mt-2 p-4 rounded-lg border bg-muted text-sm whitespace-pre-wrap">
                  {previewTemplate.text_body}
                </pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
